import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { HttpBaseService, SnackbarMsgService } from 'app/shared/services';

import { IUser } from '../shared';
import { UserResourceParameters, UsersLoadingEnum, IUserClientApplication, IUserForCreation, IUserForEdit, IUserClientApplicationForEdit } from './@shared';
import { environment } from 'environments/environment';
import { IUserRole } from './@shared/models/user-roles.interface';
import { IUserRoleForCreating } from './@shared/models/user-role-for-creating.interface';
import { IUserPermission } from './@shared/models/user-permissions.interface';
import { IUserPermissionForCreating } from './@shared/models/user-permission-for-creating.interface';
import { IImportResult } from './@shared/models/import-result.interface';

@Injectable({
  providedIn: 'root'
})
export class UsersApiService extends HttpBaseService {
  constructor() {
    super(inject(HttpClient), inject(SnackbarMsgService));
  }

  getUsers(params: UserResourceParameters = null, loadingType: UsersLoadingEnum = UsersLoadingEnum.Loading) {
    const queryParams = params ? new HttpParams({fromObject: params}) : new HttpParams();

    return this._http.get<IUser[]>(environment.apiUrl + 'users', {
      params: queryParams,
      observe: "response"
    }).pipe(
      this._handleError(false),
      this._mapPagination()
    );
  }

  getUser(userId: string, loadingType: UsersLoadingEnum = UsersLoadingEnum.Loading) {
    return this._http.get<IUser>(environment.apiUrl + 'users/' + userId).pipe(
      this._handleError()
    );
  }

  getAccessesForUser(userId: string, loadingType: UsersLoadingEnum = UsersLoadingEnum.Loading) {
    return this._http.get<IUserClientApplication[]>(environment.apiUrl + 'clients/applications/access/user/' + userId).pipe(
      this._handleError(false)
    );
  }

  postUser(user: IUserForCreation, loadingType: UsersLoadingEnum = UsersLoadingEnum.Loading) {
    return this._http.post<IUser>(environment.apiUrl + 'users', user).pipe(
      this._handleError()
    );
  }

  putUser(id: string, user: IUserForEdit, loadingType: UsersLoadingEnum = UsersLoadingEnum.Loading) {
    return this._http.put<IUser>(environment.apiUrl + 'users/' + id, user).pipe(
      this._handleError()
    );
  }

  deleteUser(id: string, loadingType: UsersLoadingEnum = UsersLoadingEnum.Loading) {
    return this._http.delete<void>(environment.apiUrl + 'users/' + id).pipe(
      this._handleError()
    );
  }

  putUserStatus(id: string, loadingType: UsersLoadingEnum = UsersLoadingEnum.Loading) {
    return this._http.put<void>(environment.apiUrl + 'users/' + id + '/status', {}).pipe(
      this._handleError()
    );
  }

  resetPassword(id: string, loadingType: UsersLoadingEnum = UsersLoadingEnum.Loading) {
    return this._http.post<void>(environment.apiUrl + 'users/' + id + '/reset-password', {}).pipe(
      this._handleError()
    );
  }

  updateAccess(userId: string, accesses: IUserClientApplicationForEdit[], loadingType: UsersLoadingEnum = UsersLoadingEnum.Loading) {
    return this._http.put<IUserClientApplication[]>(environment.apiUrl + 'clients/applications/access/user/' + userId, {
      accessesToEdit: accesses
    }).pipe(
      this._handleError(true)
    );
  }

  deleteAccess(accessId: string, loadingType: UsersLoadingEnum = UsersLoadingEnum.Loading) {
    return this._http.delete<void>(environment.apiUrl + 'clients/applications/access/' + accessId).pipe(
      this._handleError(true)
    );
  }

  getUserRoles(userId: string) {
    return this._http.get<IUserRole[]>(environment.apiUrl + `roles-users?userIds=${userId}`).pipe(
      this._handleError()
    );
  }

  createUserRole(userIds: string[], roleIds: string[], loadingType: UsersLoadingEnum = UsersLoadingEnum.Loading) {
    const payload = {
        userIds: userIds,
        roleIds: roleIds
    };

    return this._http.post<void>(
        environment.apiUrl + 'roles-users', 
        payload  
    ).pipe(
        this._handleError()
    );
}

 deleteUserRole(id:string){
  return this._http.delete<void>(environment.apiUrl + 'roles-users/' + id).pipe(
    this._handleError()
  );
 }

  getUserPermissions(userId: string, clientApplicationId?: string) {
    let url = environment.apiUrl + `permissions-users?userIds=${userId}`;
    if (clientApplicationId) {
      url += `&clientApplicationId=${clientApplicationId}`;
    }
    return this._http.get<IUserPermission[]>(url).pipe(
      this._handleError()
    );
  }

  createUserPermission(userIds: string[], permissionIds: string[]) {
    const payload: IUserPermissionForCreating = {
      userIds,
      permissionIds
    };

    return this._http.post<void>(
      environment.apiUrl + 'permissions-users',
      payload
    ).pipe(
      this._handleError()
    );
  }

  deleteUserPermission(id: string) {
    return this._http.delete<void>(environment.apiUrl + 'permissions-users/' + id).pipe(
      this._handleError()
    );
  }

  importUsers(file: File, loadingType: UsersLoadingEnum = UsersLoadingEnum.Importing) {
    const formData = new FormData();
    formData.append('file', file);

    return this._http.post<IImportResult>(environment.apiUrl + 'Users/bulk-import', formData).pipe(
      this._handleError(false)
    );
  }
}
